import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { StripeRepository } from "@/infrastructure/repositories/StripeRepository";
import { Crown, CalendarClock, CircleDollarSign, ShieldCheck } from "lucide-react";
import { ProSubscriptionProduct } from "./ProSubscriptionProduct";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alertDialog";
import { CurrentSubscriptionData } from "@/types";


const formatPrice = (amount?: number, currency?: string) => {
  if (typeof amount !== "number") return "--";
  const code = String(currency || "usd").toUpperCase();
  return `${amount} ${code}`;
};

const prettyDate = (raw?: string) => {
  if (!raw) return "--";
  const date = new Date(raw.replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return raw;
  return date.toLocaleString();
};

const statusClasses = (status?: string) => {
  const normalized = String(status || "").toLowerCase();
  if (["active", "trialing"].includes(normalized)) {
    return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300";
  }
  if (["canceled", "cancelled", "inactive", "expired"].includes(normalized)) {
    return "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300";
  }
  return "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300";
};
const isActiveLikeStatus = (status?: string) =>
  ["active", "trialing"].includes(String(status || "").toLowerCase());

const toBooleanFlag = (value: unknown): boolean => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "1" || normalized === "true";
};

export function CurrentPlanContent() {
  const stripeRepository = useMemo(() => new StripeRepository(), []);
  const [loading, setLoading] = useState(true);
  const [cancelLoadingMode, setCancelLoadingMode] = useState<"period_end" | "immediate" | null>(null);
  const [confirmMode, setConfirmMode] = useState<"period_end" | "immediate" | null>(null);
  const [plan, setPlan] = useState<CurrentSubscriptionData | null>(null);
  const [showPaywall, setShowPaywall] = useState(false);

  const loadCurrentPlan = async () => {
    try {
      setLoading(true);
      const userId =
        localStorage.getItem("current_user_swimmer_id") ||
        localStorage.getItem("selected_swimmer_id") ||
        "";

      if (!userId) {
        setPlan(null);
        return;
      }

      const response = await stripeRepository.getCurrentSubscription(String(userId));
      const payload = response?.response ?? response ?? {};
      const data = payload?.data ?? null;

      if (Number(payload?.status) !== 1 || !data || typeof data !== "object") {
        setPlan(null);
        return;
      }

      setPlan(data);
    } catch (error) {
      console.error("Failed to load current plan:", error);
      setPlan(null);
      toast.error("Unable to load current plan");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentPlan();
  }, []);

  const isCancelAtPeriodEnd = toBooleanFlag(plan?.cancel_at_period_end);

  const handleCancelSubscription = async (mode: "period_end" | "immediate") => {
    if (!plan) return;
    const subscriptionId = plan.stripe_subscription_id;

    if (!subscriptionId) {
      toast.error("Subscription id not found");
      return;
    }

    try {
      setConfirmMode(null);
      setCancelLoadingMode(mode);
      const ok = await stripeRepository.cancelSubscription({
        subscription_id: subscriptionId,
        cancel_at_period_end: mode === "period_end" ? 1 : 0,
      });

      if (!ok) {
        toast.error("Failed to cancel subscription");
        return;
      }

      toast.success(
        mode === "period_end"
          ? "Subscription will cancel at period end"
          : "Subscription canceled immediately"
      );
      await loadCurrentPlan();
    } catch (error) {
      console.error("Failed to cancel subscription:", error);
      toast.error("Unable to cancel subscription");
    } finally {
      setCancelLoadingMode(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-6 md:p-7 shadow-sm mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-gray-500 dark:text-gray-400 text-xs font-medium tracking-wide uppercase">
              Subscription
            </p>
            <h1 className="text-2xl md:text-3xl font-semibold text-gray-900 dark:text-white mt-1">
              Current Plan
            </h1>
            <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl">
              Manage billing status and cancellation preferences in one place.
            </p>
          </div>
          <div className="hidden md:flex h-12 w-12 rounded-lg bg-gray-100 dark:bg-gray-800 items-center justify-center border border-gray-200 dark:border-gray-700">
            <Crown className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </div>
        </div>
      </div>

      {loading ? (
        <div className="p-8 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
          <p className="text-gray-600 dark:text-gray-300 text-lg">Loading current plan...</p>
        </div>
      ) : !plan ? (
        <>
          {showPaywall ? (
            <div className="p-2 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
              <ProSubscriptionProduct onClose={() => setShowPaywall(false)} />
            </div>
          ) : (
            <div className="p-8 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
              <p className="text-gray-700 dark:text-gray-200 text-lg mb-4">No active subscription found.</p>
              <button
                onClick={() => setShowPaywall(true)}
                className="px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium"
              >
                View Plans
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="space-y-6">
          <div className="p-6 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Product</p>
                <p className="text-2xl font-bold dark:text-white mt-1">
                  {plan.product_name || "Swimnerd Plan"}
                </p>
              </div>
              <span
                className={`px-3 py-1 rounded-full text-sm font-semibold ${statusClasses(
                  plan.status
                )}`}
              >
                {plan.status || "--"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-5 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <CircleDollarSign className="h-4 w-4" />
                <p className="text-sm">Amount</p>
              </div>
              <p className="text-xl font-bold dark:text-white mt-2">
                {formatPrice(plan.amount_total ?? plan.amount, plan.currency)}
              </p>
            </div>

            <div className="p-5 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <CalendarClock className="h-4 w-4" />
                <p className="text-sm">Current Period End</p>
              </div>
              <p className="text-base font-semibold dark:text-white mt-2">
                {prettyDate(plan.current_period_end)}
              </p>
            </div>

            <div className="p-5 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
              <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                <ShieldCheck className="h-4 w-4" />
                <p className="text-sm">Renewal Date</p>
              </div>
              <p className="text-base font-semibold dark:text-white mt-2">
                {prettyDate(plan.renewal_date)}
              </p>
            </div>
          </div>

          <div className="p-6 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
            <p className="text-sm text-gray-500 mb-2">Current Period</p>
            <p className="font-medium dark:text-white">
              {prettyDate(plan.current_period_start)} to {prettyDate(plan.current_period_end)}
            </p>
          </div>

          <div className="p-6 border rounded-2xl bg-white dark:bg-gray-900 dark:border-gray-700 shadow-sm">
            <p className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-4">
              Cancellation Options
            </p>
            {isCancelAtPeriodEnd && (
              <p className="text-amber-600 font-medium mb-3">
                Cancellation is already scheduled at period end.
              </p>
            )}
            {isActiveLikeStatus(plan.status) ? (
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setConfirmMode("period_end")}
                  disabled={cancelLoadingMode !== null || isCancelAtPeriodEnd}
                  className={`px-4 py-2 rounded-lg text-white ${
                    cancelLoadingMode !== null || isCancelAtPeriodEnd
                      ? "bg-gray-400"
                      : "bg-amber-600 hover:bg-amber-700"
                  }`}
                >
                  {isCancelAtPeriodEnd
                    ? "Already Scheduled"
                    : cancelLoadingMode === "period_end"
                    ? "Please wait..."
                    : "Cancel at Period End"}
                </button>

                <button
                  onClick={() => setConfirmMode("immediate")}
                  disabled={cancelLoadingMode !== null}
                  className={`px-4 py-2 rounded-lg text-white ${
                    cancelLoadingMode !== null ? "bg-gray-400" : "bg-red-600 hover:bg-red-700"
                  }`}
                >
                  {cancelLoadingMode === "immediate" ? "Please wait..." : "Cancel Immediately"}
                </button>
              </div>
            ) : (
              <p className="text-muted-foreground">
                Cancellation actions are unavailable for this subscription status.
              </p>
            )}
          </div>
        </div>
      )}

      <AlertDialog open={confirmMode !== null}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm cancellation</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmMode === "period_end"
                ? "Your plan will remain active until the end of the current billing period. Continue?"
                : "Your subscription will be canceled immediately. Continue?"}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmMode(null)}>No</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmMode) {
                  handleCancelSubscription(confirmMode);
                }
              }}
            >
              Yes, cancel
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
